import { GenericTokenList } from '../../types'
import { MulticallRetryFunction } from '../../types/providers'
import {
  fetchEulerEarnVaultsFromSubgraph,
  getEulerEarnSubgraphUrl,
} from './fetchFromSubgraph'
import { fetchEulerEarnVaultsFromApi } from './fetchFromApi'
import { EulerEarnVaults } from './types'

const isNonEmpty = (v: EulerEarnVaults | undefined): v is EulerEarnVaults =>
  !!v && Object.keys(v).length > 0

/**
 * One-shot fetcher for all Euler Earn vaults on a chain.
 *
 * Source strategy — Euler Data API primary, Goldsky subgraph fallback:
 *  - The Euler Data API reads live on-chain figures (APR-converted rates,
 *    correct share decimals, cap-aware liquidity), so it's the source of
 *    record. Used first on every chain.
 *  - The Goldsky subgraph is the fallback when the API is unavailable or
 *    returns nothing for the chain (and only on chains that have a
 *    subgraph configured). It can lag chain head, so it's no longer the
 *    primary path.
 *
 * @param chainId        target chain
 * @param prices         optional price map keyed by oracle key / underlying
 * @param tokenList      optional token list for hydrating `asset` metadata
 * @param multicallRetry optional multicall executor — both paths use it to
 *                       read each earn vault's real `decimals()` on-chain
 *                       (the Data API's `decimals` field is unreliable)
 *
 * @returns `EulerEarnVaults` keyed by lowercased vault address — `{}` when
 *          neither source returns data.
 */
export const fetchEulerEarnVaults = async (
  chainId: string,
  prices: { [asset: string]: number } = {},
  tokenList: GenericTokenList = {},
  multicallRetry?: MulticallRetryFunction,
): Promise<EulerEarnVaults> => {
  // API first — live, APR-correct, decimals-correct.
  const fromApi = await fetchEulerEarnVaultsFromApi(
    chainId,
    prices,
    tokenList,
    multicallRetry,
  ).catch(() => undefined)
  if (isNonEmpty(fromApi)) return fromApi

  // Fallback: the subgraph, only where one is configured.
  const subgraphUrl = getEulerEarnSubgraphUrl(chainId)
  if (!subgraphUrl) return fromApi ?? {}

  const fromSubgraph = await fetchEulerEarnVaultsFromSubgraph(
    chainId,
    prices,
    tokenList,
    multicallRetry,
  ).catch(() => undefined)
  return isNonEmpty(fromSubgraph) ? fromSubgraph : (fromApi ?? {})
}
